import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Message } from '../types/chat';
import { apiClient } from '../utils/apiClient';

interface ChatContextType {
  messages: Message[];
  loading: boolean;
  isLoadingHistory: boolean;
  hasMoreHistory: boolean;
  historyOffset: number;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  setLoading: (loading: boolean) => void;
  setIsLoadingHistory: (loading: boolean) => void;
  setHasMoreHistory: (hasMore: boolean) => void;
  setHistoryOffset: (offset: number) => void;
  loadChatHistory: () => Promise<void>;
  loadOlderHistory: () => Promise<void>;
  clearChatHistory: () => Promise<void>;
  addMessage: (message: Message) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: React.ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { authState } = useAuth();
  const user = authState.user;
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [historyOffset, setHistoryOffset] = useState(5);

  // Load recent chat history (initial 5 conversations)
  const loadChatHistory = useCallback(async () => {
    if (!user?.userId) {
      return;
    }

    try {
      const response = await apiClient.get(`/api/chat/history/${user.userId}?limit=5`);
      
      const data = await response.json();
      
      if (data.success || (Array.isArray(data.chats) && data.chats.length > 0)) {
        if (data.chats && data.chats.length > 0) {
          const loadedMessages: Message[] = [];
          data.chats.reverse().forEach((chat: any) => {
            const userMsgId = chat.id ? `${chat.id}_user` : `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const botMsgId = chat.id ? `${chat.id}_bot` : `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            loadedMessages.push(
              { role: 'user', text: chat.userMessage, messageId: userMsgId, isFromHistory: true },
              { 
                role: 'bot', 
                text: chat.botReply, 
                messageId: botMsgId,
                suggestions: chat.suggestions,
                category: chat.category,
                results: chat.results || [],
                isFromHistory: true,
                isCurated: chat.category === 'CURATED'
              }
            );
          });
          setMessages(loadedMessages);
        }
        setHasMoreHistory(data.hasMore || false);
      }
    } catch (error) {
      // Error handled silently
    }
  }, [user?.userId]);

  // Load older history (pagination)
  const loadOlderHistory = useCallback(async () => {
    if (!user?.userId || !hasMoreHistory || isLoadingHistory) return;
    
    setIsLoadingHistory(true);
    
    try {
      const response = await apiClient.get(`/api/chat/history/${user.userId}?limit=5&offset=${historyOffset}`);
      
      const data = await response.json();
      
      if (data.success && data.chats && data.chats.length > 0) {
        const olderMessages: Message[] = [];
        data.chats.reverse().forEach((chat: any) => {
          const userMsgId = chat.id ? `${chat.id}_user` : `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const botMsgId = chat.id ? `${chat.id}_bot` : `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          
          olderMessages.push(
            { role: 'user', text: chat.userMessage, messageId: userMsgId, isFromHistory: true },
            { 
              role: 'bot', 
              text: chat.botReply, 
              messageId: botMsgId,
              suggestions: chat.suggestions,
              category: chat.category,
              results: chat.results || [],
              isFromHistory: true,
              isCurated: chat.category === 'CURATED'
            }
          );
        });
        
        // Prepend older messages
        setMessages(prev => [...olderMessages, ...prev]);
        setHistoryOffset(prev => prev + 5);
        setHasMoreHistory(data.hasMore || false);
      } else {
        setHasMoreHistory(false);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user?.userId, hasMoreHistory, isLoadingHistory, historyOffset]);

  // Clear chat history (backend + local state)
  const clearChatHistory = useCallback(async () => {
    // Clear backend Firestore data for authenticated users
    if (authState.isAuthenticated && user?.userId) {
      await apiClient.delete(`/api/auth/clear-chat-history/${user.userId}`);
    }
    
    // Clear localStorage (for unregistered users)
    localStorage.removeItem('hey-sl-chat-history');
    
    // Clear local state - THIS IS THE KEY!
    setMessages([]);
    setHasMoreHistory(true);
    setHistoryOffset(5);
    
  }, [authState.isAuthenticated, user?.userId]);

  // Add a single message
  const addMessage = useCallback((message: Message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  // Load chat history when user logs in
  useEffect(() => {
    if (user?.userId) {
      setIsLoadingHistory(true);
      loadChatHistory().finally(() => {
        setIsLoadingHistory(false);
      });
    } else {
      // Clear messages when user logs out
      setMessages([]);
      setHasMoreHistory(true);
      setHistoryOffset(5);
    }
  }, [user?.userId, loadChatHistory]);

  const value: ChatContextType = {
    messages,
    loading,
    isLoadingHistory,
    hasMoreHistory,
    historyOffset,
    setMessages,
    setLoading,
    setIsLoadingHistory,
    setHasMoreHistory,
    setHistoryOffset,
    loadChatHistory,
    loadOlderHistory,
    clearChatHistory,
    addMessage,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

